import { useState, useEffect } from 'react';
import { LogOut, BarChart3, FolderOpen, Shield, Clock3, ChevronDown } from 'lucide-react';
import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarHeader,
  SidebarInset,
  SidebarTrigger,
} from './ui/sidebar';
import { ReportsScreen } from './admin/ReportsScreen';
import { CategoriesAndTasksScreen } from './admin/CategoriesAndTasksScreen';
import type { Vendor, PINCode } from './admin/VendorsScreen';
import type { SupervisorAccess } from './admin/PINCodesScreen';
import { ManageAccessScreen } from './admin/ManageAccessScreen';
import { TimesheetsScreen } from './admin/time-tracking/TimesheetsScreen';
import type { Task, Category, TaskSubmission } from '../App';
import type { Project } from '../types/project';
import type { Operator } from '../types/timeTracking';
import * as api from '../utils/api';

interface AdminDashboardProps {
  tasks: Task[];
  categories: Category[];
  projects: Project[];
  submissions: TaskSubmission[];
  onAddTask: (task: Task) => void;
  onUpdateTask: (task: Task) => void;
  onDeleteTask: (taskId: string) => void;
  onAddCategory: (category: Category) => void;
  onAddProject: (project: Project) => void;
  onUpdateProject: (project: Project) => void;
  onDeleteProject: (projectId: string) => void;
  onLogout: () => void;
}

type MenuItem = 'reports' | 'categories-tasks' | 'pin-codes' | 'time-tracking-timesheets';

// Mock vendors and PIN codes data
const INITIAL_VENDORS: Vendor[] = [
  { id: 'vendor-1', name: 'ABC Construction', pinCodes: [] },
  { id: 'vendor-2', name: 'XYZ Engineering', pinCodes: [] },
];

const INITIAL_PINCODES: PINCode[] = [
  { id: 'pin-1', code: '1234', vendorId: 'vendor-1', vendorName: 'ABC Construction', isActive: true, projects: ['proj-1'] },
  { id: 'pin-2', code: '5678', vendorId: 'vendor-2', vendorName: 'XYZ Engineering', isActive: true, projects: ['proj-1'] },
  { id: 'pin-3', code: '9999', vendorId: 'gama-operator', vendorName: 'Gama Operator', isActive: true, projects: ['proj-1'] },
];

const INITIAL_SUPERVISOR_ACCESS: SupervisorAccess = {
  id: 'supervisor-access',
  projects: ['proj-1']
};

const OPERATORS_STORAGE_KEY = 'operators';

const readLocalOperators = (): Operator[] => {
  try {
    const stored = localStorage.getItem(OPERATORS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const writeLocalOperators = (operators: Operator[]) => {
  localStorage.setItem(OPERATORS_STORAGE_KEY, JSON.stringify(operators));
  sessionStorage.setItem('operators', JSON.stringify(operators));
};

export function AdminDashboard({ 
  tasks, 
  categories,
  projects,
  submissions,
  onAddTask, 
  onUpdateTask, 
  onDeleteTask, 
  onAddCategory,
  onAddProject,
  onUpdateProject,
  onDeleteProject,
  onLogout 
}: AdminDashboardProps) {
  const [activeScreen, setActiveScreen] = useState<MenuItem>('categories-tasks');
  const [vendors, setVendors] = useState<Vendor[]>(INITIAL_VENDORS);
  const [pinCodes, setPinCodes] = useState<PINCode[]>(INITIAL_PINCODES);
  const [supervisorAccess, setSupervisorAccess] = useState<SupervisorAccess | null>(INITIAL_SUPERVISOR_ACCESS);
  const [microsoftAccounts, setMicrosoftAccounts] = useState<any[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [timeTrackingExpanded, setTimeTrackingExpanded] = useState(true);

  // Load vendors, PIN codes, and supervisor access from backend on mount
  useEffect(() => {
    loadVendorsAndPINCodes();
  }, []);

  const loadVendorsAndPINCodes = async () => {
    // Operators are stored locally — load them first regardless of backend result
    const localOperators = readLocalOperators();
    setOperators(localOperators);
    writeLocalOperators(localOperators);

    try {
      const [loadedVendors, loadedPINCodes, loadedSupervisorAccess, loadedMicrosoftAccounts] = await Promise.all([
        api.getVendors(),
        api.getPINCodes(),
        api.getSupervisorAccess(),
        api.getMicrosoftAccounts(),
      ]);

      // If database is empty, initialize with defaults
      if (loadedVendors.length === 0 && loadedPINCodes.length === 0) {
        await api.initializeDatabase({
          vendors: INITIAL_VENDORS,
          pincodes: INITIAL_PINCODES,
          supervisorAccess: INITIAL_SUPERVISOR_ACCESS,
        });

        const [newVendors, newPINCodes, newSupervisorAccess, newMicrosoftAccounts] = await Promise.all([
          api.getVendors(),
          api.getPINCodes(),
          api.getSupervisorAccess(),
          api.getMicrosoftAccounts(),
        ]);

        setVendors(newVendors.length > 0 ? newVendors : INITIAL_VENDORS);
        setPinCodes(newPINCodes.length > 0 ? newPINCodes : INITIAL_PINCODES);
        setSupervisorAccess(newSupervisorAccess || INITIAL_SUPERVISOR_ACCESS);
        setMicrosoftAccounts(newMicrosoftAccounts || []);
        sessionStorage.setItem('pinCodes', JSON.stringify(newPINCodes.length > 0 ? newPINCodes : INITIAL_PINCODES));
        sessionStorage.setItem('supervisorAccess', JSON.stringify(newSupervisorAccess || INITIAL_SUPERVISOR_ACCESS));
        sessionStorage.setItem('microsoftAccounts', JSON.stringify(newMicrosoftAccounts || []));
      } else {
        setVendors(loadedVendors.length > 0 ? loadedVendors : INITIAL_VENDORS);
        setPinCodes(loadedPINCodes.length > 0 ? loadedPINCodes : INITIAL_PINCODES);
        setSupervisorAccess(loadedSupervisorAccess || INITIAL_SUPERVISOR_ACCESS);
        setMicrosoftAccounts(loadedMicrosoftAccounts || []);
        sessionStorage.setItem('pinCodes', JSON.stringify(loadedPINCodes));
        sessionStorage.setItem('supervisorAccess', JSON.stringify(loadedSupervisorAccess));
        sessionStorage.setItem('microsoftAccounts', JSON.stringify(loadedMicrosoftAccounts || []));
      }
    } catch (error) {
      console.error('Error loading vendors and PIN codes from backend:', error);
      setVendors(INITIAL_VENDORS);
      setPinCodes(INITIAL_PINCODES);
      setSupervisorAccess(INITIAL_SUPERVISOR_ACCESS);
      setMicrosoftAccounts([]);
      sessionStorage.setItem('pinCodes', JSON.stringify(INITIAL_PINCODES));
      sessionStorage.setItem('supervisorAccess', JSON.stringify(INITIAL_SUPERVISOR_ACCESS));
      sessionStorage.setItem('microsoftAccounts', JSON.stringify([]));
    }
  };

  const menuItems = [
    { id: 'reports' as MenuItem, label: 'Reports and Projects', icon: BarChart3 },
    { id: 'categories-tasks' as MenuItem, label: 'Categories & Tasks', icon: FolderOpen },
    { id: 'pin-codes' as MenuItem, label: 'Manage Access', icon: Shield },
  ];

  const timeTrackingItems = [
    { id: 'time-tracking-timesheets' as MenuItem, label: 'Timesheets', icon: Clock3 },
  ];


  const handleAddVendor = (vendor: Vendor) => {
    setVendors([...vendors, vendor]);
    // Save to backend
    api.saveVendor(vendor).catch(error => {
      console.error('Error saving vendor to backend:', error);
    });
  };

  const handleUpdateVendor = (updatedVendor: Vendor) => {
    setVendors(vendors.map(v => v.id === updatedVendor.id ? updatedVendor : v));
    // Save to backend
    api.saveVendor(updatedVendor).catch(error => {
      console.error('Error updating vendor in backend:', error);
    });
  };

  const handleDeleteVendor = (vendorId: string) => {
    setVendors(vendors.filter(v => v.id !== vendorId));
    // Also delete associated PIN codes
    const associatedPINs = pinCodes.filter(pin => pin.vendorId === vendorId);
    setPinCodes(pinCodes.filter(pin => pin.vendorId !== vendorId));
    
    // Delete from backend
    api.deleteVendor(vendorId).catch(error => {
      console.error('Error deleting vendor from backend:', error);
    });
    
    // Delete associated PIN codes from backend
    associatedPINs.forEach(pin => {
      api.deletePINCode(pin.id).catch(error => {
        console.error('Error deleting associated PIN code from backend:', error);
      });
    });
  };

  const handleAddPINCode = (pinCode: PINCode) => {
    const updated = [...pinCodes, pinCode];
    setPinCodes(updated);
    // Save to sessionStorage for login
    sessionStorage.setItem('pinCodes', JSON.stringify(updated));
    // Save to backend
    api.savePINCode(pinCode).catch(error => {
      console.error('Error saving PIN code to backend:', error);
    });
  };

  const handleUpdatePINCode = (pinCode: PINCode) => {
    const updated = pinCodes.map(pin => pin.id === pinCode.id ? pinCode : pin);
    setPinCodes(updated);
    // Save to sessionStorage for login
    sessionStorage.setItem('pinCodes', JSON.stringify(updated));
    // Save to backend
    api.savePINCode(pinCode).catch(error => {
      console.error('Error updating PIN code in backend:', error);
    });
  };

  const handleDeletePINCode = (pinCodeId: string) => {
    const updated = pinCodes.filter(pin => pin.id !== pinCodeId);
    setPinCodes(updated);
    // Save to sessionStorage for login
    sessionStorage.setItem('pinCodes', JSON.stringify(updated));
    // Delete from backend
    api.deletePINCode(pinCodeId).catch(error => {
      console.error('Error deleting PIN code from backend:', error);
    });
  };

  const handleTogglePINStatus = (pinCodeId: string) => {
    const updated = pinCodes.map(pin => 
      pin.id === pinCodeId ? { ...pin, isActive: !pin.isActive } : pin
    );
    setPinCodes(updated);
    // Save to sessionStorage for login
    sessionStorage.setItem('pinCodes', JSON.stringify(updated));
    // Save to backend
    const toggledPIN = updated.find(pin => pin.id === pinCodeId);
    if (toggledPIN) {
      api.savePINCode(toggledPIN).catch(error => {
        console.error('Error toggling PIN code status in backend:', error);
      });
    }
  };

  const handleUpdateSupervisorAccess = (access: SupervisorAccess) => {
    setSupervisorAccess(access);
    // Save to sessionStorage for login
    sessionStorage.setItem('supervisorAccess', JSON.stringify(access));
    // Save to backend
    api.saveSupervisorAccess(access).catch(error => {
      console.error('Error saving supervisor access to backend:', error);
    });
  };

  // Microsoft account handlers
  const handleAddMicrosoftAccount = (account: any) => {
    const updated = [...microsoftAccounts, account];
    setMicrosoftAccounts(updated);
    // Save to sessionStorage for login
    sessionStorage.setItem('microsoftAccounts', JSON.stringify(updated));
    // Save to backend
    api.saveMicrosoftAccount(account).catch(error => {
      console.error('Error saving Microsoft account to backend:', error);
    });
  };

  const handleUpdateMicrosoftAccount = (account: any) => {
    const updated = microsoftAccounts.map(a => a.id === account.id ? account : a);
    setMicrosoftAccounts(updated);
    // Save to sessionStorage for login
    sessionStorage.setItem('microsoftAccounts', JSON.stringify(updated));
    // Save to backend
    api.saveMicrosoftAccount(account).catch(error => {
      console.error('Error updating Microsoft account in backend:', error);
    });
  };

  const handleDeleteMicrosoftAccount = (accountId: string) => {
    const updated = microsoftAccounts.filter(a => a.id !== accountId);
    setMicrosoftAccounts(updated);
    // Save to sessionStorage for login
    sessionStorage.setItem('microsoftAccounts', JSON.stringify(updated));
    // Delete from backend
    api.deleteMicrosoftAccount(accountId).catch(error => {
      console.error('Error deleting Microsoft account from backend:', error);
    });
  };


  const handleAddOperator = (operator: Operator) => {
    const updated = [...operators, operator];
    setOperators(updated);
    writeLocalOperators(updated);
  };

  const handleAddOperators = (newOperators: Operator[]) => {
    const updated = [...operators, ...newOperators];
    setOperators(updated);
    writeLocalOperators(updated);
  };

  const handleUpdateOperator = (operator: Operator) => {
    const updated = operators.map(item => item.id === operator.id ? operator : item);
    setOperators(updated);
    writeLocalOperators(updated);
  };

  const handleDeleteOperator = (operatorId: string) => {
    const updated = operators.filter(op => op.id !== operatorId);
    setOperators(updated);
    writeLocalOperators(updated);
  };

  const handleUpdateCategory = (updatedCategory: Category) => {
    onAddCategory(updatedCategory);
  };

  const handleDeleteCategory = (categoryId: string) => {
    // For now, just alert - in a real app, you'd want to handle this properly
    console.log('Delete category:', categoryId);
  };

  const renderScreen = () => {

    switch (activeScreen) {
      case 'reports':
        return (
          <ReportsScreen
            categories={categories}
            tasks={tasks}
            projects={projects}
            submissions={submissions}
            pinCodes={pinCodes}
            supervisorAccess={supervisorAccess}
            microsoftAccounts={microsoftAccounts}
            onAddProject={onAddProject}
            onUpdateProject={onUpdateProject}
            onDeleteProject={onDeleteProject}
            onNavigateToManageAccess={() => setActiveScreen('pin-codes')}
          />
        );
      
      case 'categories-tasks':
        return (
          <CategoriesAndTasksScreen
            categories={categories}
            tasks={tasks}
            vendors={vendors}
            microsoftAccounts={microsoftAccounts}
            onAddCategory={onAddCategory}
            onUpdateCategory={handleUpdateCategory}
            onDeleteCategory={handleDeleteCategory}
            onAddTask={onAddTask}
            onUpdateTask={onUpdateTask}
            onDeleteTask={onDeleteTask}
          />
        );
      
      case 'pin-codes':
        return (
          <ManageAccessScreen
            vendors={vendors}
            pinCodes={pinCodes}
            projects={projects}
            supervisorAccess={supervisorAccess}
            microsoftAccounts={microsoftAccounts}
            operators={operators}
            onAddVendor={handleAddVendor}
            onUpdateVendor={handleUpdateVendor}
            onDeleteVendor={handleDeleteVendor}
            onAddPINCode={handleAddPINCode}
            onUpdatePINCode={handleUpdatePINCode}
            onDeletePINCode={handleDeletePINCode}
            onTogglePINStatus={handleTogglePINStatus}
            onUpdateSupervisorAccess={handleUpdateSupervisorAccess}
            onAddMicrosoftAccount={handleAddMicrosoftAccount}
            onUpdateMicrosoftAccount={handleUpdateMicrosoftAccount}
            onDeleteMicrosoftAccount={handleDeleteMicrosoftAccount}
            onAddOperator={handleAddOperator}
            onAddOperators={handleAddOperators}
            onUpdateOperator={handleUpdateOperator}
            onDeleteOperator={handleDeleteOperator}
          />
        );

      case 'time-tracking-timesheets':
        return <TimesheetsScreen operators={operators} projects={projects} microsoftAccounts={microsoftAccounts} />;
      
      default:
        return (
          <ReportsScreen
            categories={categories}
            tasks={tasks}
            projects={projects}
            submissions={submissions}
            pinCodes={pinCodes}
            supervisorAccess={supervisorAccess}
            microsoftAccounts={microsoftAccounts}
            onAddProject={onAddProject}
            onUpdateProject={onUpdateProject}
            onDeleteProject={onDeleteProject}
            onNavigateToManageAccess={() => setActiveScreen('pin-codes')}
          />
        );
    }
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <Sidebar collapsible={"icon"}>
          <SidebarHeader className="border-b border-gray-200 p-4">
            <div className="flex items-center gap-3 group-data-[collapsible=icon]:justify-center">
              <SidebarTrigger />
              <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                <h2 className="font-bold text-lg text-gray-900">Admin Portal</h2>
                <p className="text-xs text-gray-600">Task Management System</p>
              </div>
            </div>
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {menuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <SidebarMenuItem key={item.id}>
                        <SidebarMenuButton
                          onClick={() => setActiveScreen(item.id)}
                          isActive={activeScreen === item.id}
                          tooltip={item.label}
                        >
                          <Icon className="w-4 h-4" />
                          <span>{item.label}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={() => setTimeTrackingExpanded(prev => !prev)}
                      isActive={activeScreen === 'time-tracking-timesheets'}
                      tooltip="Time Tracking"
                    >
                      <Clock3 className="w-4 h-4" />
                      <span>Time Tracking</span>
                      <ChevronDown className={`ml-auto w-4 h-4 transition group-data-[collapsible=icon]:hidden ${timeTrackingExpanded ? 'rotate-180' : ''}`} />
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  {timeTrackingExpanded && (
                    <>
                      {timeTrackingItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <SidebarMenuItem key={item.id}>
                            <SidebarMenuButton
                              onClick={() => setActiveScreen(item.id)}
                              isActive={activeScreen === item.id}
                              tooltip={item.label}
                              className="pl-10 group-data-[collapsible=icon]:pl-2"
                            >
                              <Icon className="w-4 h-4" />
                              <span>{item.label}</span>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        );
                      })}
                    </>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
          <div className="mt-auto border-t border-gray-200 p-4 group-data-[collapsible=icon]:p-2">
            <button
              onClick={onLogout}
              className="flex items-center gap-2 w-full px-4 py-2 text-gray-700 hover:text-red-600 hover:bg-red-50 rounded-lg transition group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:w-auto group-data-[collapsible=icon]:mx-auto"
              title="Logout"
            >
              <LogOut className="w-5 h-5 group-data-[collapsible=icon]:w-5 group-data-[collapsible=icon]:h-5" />
              <span className="group-data-[collapsible=icon]:hidden">Logout</span>
            </button>
          </div>
        </Sidebar>

        <SidebarInset>
          <header className="flex h-16 shrink-0 items-center gap-2 border-b border-gray-200 bg-white px-4">
            {(() => {
              const activeItem = [...menuItems, ...timeTrackingItems].find(item => item.id === activeScreen);
              const Icon = activeItem?.icon;
              const title = activeScreen === 'time-tracking-timesheets'
                ? `Time Tracking / ${activeItem?.label || 'Overview'}`
                : activeItem?.label || 'Dashboard';
              return (
                <div className="flex items-center gap-2">
                  {Icon && <Icon className="w-5 h-5 text-gray-700" />}
                  <h1 className="text-xl font-semibold text-gray-900">
                    {title}
                  </h1>
                </div>
              );
            })()}
          </header>
          <main className="flex-1 overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100">
            {renderScreen()}
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}
